import {
  addDoc,
  collection,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { db } from "../../../firebase";
import { Account, BalanceSnapshot } from "../../../types/spend-tracker";
import {
  formatCurrency,
  formatCurrencyChange,
} from "../../../utils/spend-calculations";
import { useAuth } from "../../components/authContext";
import MetricCard from "../../components/spend-tracker/MetricCard";
import AnalyticsSection from "../../components/spend-tracker/analytics";
import EntryForm from "../../components/spend-tracker/entry-form";

interface Entry {
  id: string;
  date: string;
  balances: BalanceSnapshot[];
  netWorth: number;
  totalCash: number;
  totalCredit: number;
  totalInvestments: number;
}

const SpendDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [entries, setEntries] = useState<Entry[]>([]);
  const [selectedEntryIndex, setSelectedEntryIndex] = useState<number>(0);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewEntryForm, setShowNewEntryForm] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login-spend");
    }
  }, [user, navigate]);

  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      // Load accounts
      const accountsRef = collection(db, "users", user.uid, "accounts");
      const accountsSnap = await getDocs(accountsRef);
      const loadedAccounts = accountsSnap.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() } as Account)
      );
      setAccounts(
        loadedAccounts.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      );

      // Load all balances
      const balancesRef = collection(db, "users", user.uid, "balances");
      const balancesSnap = await getDocs(
        query(balancesRef, orderBy("date", "desc"))
      );

      // Group balances by date
      const balancesByDate = new Map<string, BalanceSnapshot[]>();
      balancesSnap.docs.forEach((doc) => {
        const balance = { id: doc.id, ...doc.data() } as BalanceSnapshot;
        const date = balance.date;
        if (!balancesByDate.has(date)) {
          balancesByDate.set(date, []);
        }
        balancesByDate.get(date)!.push(balance);
      });

      // Create entries from grouped balances
      const loadedEntries: Entry[] = Array.from(balancesByDate.entries())
        .map(([date, balances]) => {
          const summary = calculateSummary(balances, loadedAccounts);
          return {
            id: date,
            date,
            balances,
            ...summary,
          };
        })
        .sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );

      setEntries(loadedEntries);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    loadData();
  }, [user, loadData]);

  const calculateSummary = (
    balances: BalanceSnapshot[],
    accounts: Account[]
  ) => {
    let totalCash = 0;
    let totalCredit = 0;
    let totalInvestments = 0;

    balances.forEach((balance) => {
      const account = accounts.find((a) => a.id === balance.accountId);
      if (!account) return;

      if (account.type === "cash") {
        totalCash += balance.balance;
      } else if (account.type === "credit") {
        totalCredit += balance.balance;
      } else if (account.type === "investment") {
        totalInvestments += balance.balance;
      }
    });

    const netWorth = totalCash + totalCredit + totalInvestments;

    return { netWorth, totalCash, totalCredit, totalInvestments };
  };

  const handleCreateNewEntry = async (
    date: string,
    balances: Map<string, number>
  ) => {
    if (!user) return;

    try {
      // Save balances with the date
      const balancesRef = collection(db, "users", user.uid, "balances");
      for (const [accountId, balance] of Array.from(balances.entries())) {
        await addDoc(balancesRef, {
          date: date,
          accountId,
          balance,
          createdAt: serverTimestamp(),
        });
      }

      alert("New entry created successfully!");
      setShowNewEntryForm(false);
      loadData();
    } catch (error) {
      console.error("Error creating new entry:", error);
      alert("Failed to create new entry");
      throw error;
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <p style={{ color: "#888" }}>Loading...</p>
      </div>
    );
  }

  const currentEntry = entries[selectedEntryIndex];
  const previousEntry = entries[selectedEntryIndex + 1];

  // Calculate changes
  const netWorthChange =
    currentEntry && previousEntry
      ? currentEntry.netWorth - previousEntry.netWorth
      : 0;

  const estimatedSpend =
    currentEntry && previousEntry
      ? Math.max(
          0,
          previousEntry.totalCash -
            currentEntry.totalCash +
            (currentEntry.totalCredit - previousEntry.totalCredit)
        )
      : 0;

  const approxInvesting =
    currentEntry && previousEntry
      ? Math.max(
          0,
          currentEntry.totalInvestments - previousEntry.totalInvestments
        )
      : 0;

  // Prepare chart data (most recent 12 entries, reversed for chronological order)
  const chartData = entries
    .slice(0, 12)
    .reverse()
    .map((entry) => ({
      date: new Date(entry.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      netWorth: entry.netWorth,
    }));

  return (
    <div style={{ minHeight: "100vh", padding: "2rem" }}>
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "2rem" }}>
          <h1
            style={{
              fontSize: "2.25rem",
              fontWeight: "bold",
              marginBottom: "0.5rem",
            }}
          >
            Net Worth Tracker
          </h1>
          <p style={{ color: "#888" }}>
            Track your net worth over time. No Plaid, no transactions—just
            simple balance snapshots.
          </p>
        </div>

        {/* Action Buttons */}
        <div
          style={{
            marginBottom: "1.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              onClick={() => setShowNewEntryForm(!showNewEntryForm)}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "0.375rem",
                backgroundColor: showNewEntryForm ? "#6b7280" : "#2563eb",
                color: "white",
                border: "none",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              {showNewEntryForm ? "Cancel" : "+ New Entry"}
            </button>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              onClick={() => navigate("/money/accounts")}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "0.375rem",
                border: "1px solid #ccc",
                cursor: "pointer",
              }}
            >
              Manage Accounts
            </button>
            <button
              onClick={() => navigate("/money/edit")}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "0.375rem",
                border: "1px solid #ccc",
                cursor: "pointer",
              }}
            >
              Edit Entries
            </button>
          </div>
        </div>

        {/* New Entry Form */}
        {showNewEntryForm && (
          <div style={{ marginBottom: "2rem" }}>
            <EntryForm
              accounts={accounts}
              onSave={handleCreateNewEntry}
              onCancel={() => setShowNewEntryForm(false)}
              existingDates={entries.map((e) => e.date)}
            />
          </div>
        )}

        {!showNewEntryForm && entries.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem 0" }}>
            <p
              style={{
                fontSize: "1.125rem",
                color: "#888",
                marginBottom: "1rem",
              }}
            >
              No entries yet. Create your first entry to start tracking your net
              worth!
            </p>
            <button
              onClick={() => setShowNewEntryForm(true)}
              style={{
                padding: "0.75rem 1.5rem",
                borderRadius: "0.375rem",
                backgroundColor: "#2563eb",
                color: "white",
              }}
            >
              + Create First Entry
            </button>
          </div>
        ) : (
          !showNewEntryForm && (
            <>
              {/* Entry Selector */}
              <div style={{ marginBottom: "1.5rem" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    marginBottom: "0.5rem",
                  }}
                >
                  View Entry
                </label>
                <select
                  value={selectedEntryIndex}
                  onChange={(e) =>
                    setSelectedEntryIndex(parseInt(e.target.value))
                  }
                  style={{
                    padding: "0.5rem 1rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #ccc",
                    fontSize: "1rem",
                  }}
                >
                  {entries.map((entry, index) => (
                    <option key={entry.id} value={index}>
                      {new Date(entry.date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </option>
                  ))}
                </select>
              </div>

              {/* Metric Cards */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "1rem",
                  marginBottom: "2rem",
                }}
              >
                <MetricCard
                  title="Net Worth"
                  value={formatCurrency(currentEntry?.netWorth || 0)}
                  subtitle={
                    currentEntry
                      ? new Date(currentEntry.date).toLocaleDateString()
                      : ""
                  }
                />
                <MetricCard
                  title="Change vs Previous"
                  value={
                    previousEntry ? formatCurrencyChange(netWorthChange) : "N/A"
                  }
                  change={
                    previousEntry
                      ? netWorthChange >= 0
                        ? "Increase"
                        : "Decrease"
                      : undefined
                  }
                  changeType={netWorthChange >= 0 ? "positive" : "negative"}
                />
                <MetricCard
                  title="Estimated Spend"
                  value={formatCurrency(estimatedSpend)}
                  subtitle="Based on balance changes"
                />
                <MetricCard
                  title="Approx. Investing"
                  value={formatCurrency(approxInvesting)}
                  subtitle="Investment increases"
                />
              </div>

              {/* Net Worth Chart */}
              {chartData.length > 0 && (
                <div
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "0.5rem",
                    border: "1px solid #e5e7eb",
                    padding: "1.5rem",
                    marginBottom: "2rem",
                  }}
                >
                  <h2
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: "600",
                      marginBottom: "1rem",
                    }}
                  >
                    Net Worth Over Time
                  </h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" style={{ fontSize: "0.75rem" }} />
                      <YAxis
                        tickFormatter={(value) =>
                          `$${(value / 1000).toFixed(0)}k`
                        }
                        style={{ fontSize: "0.75rem" }}
                      />
                      <Tooltip
                        formatter={(value: number) => formatCurrency(value)}
                        contentStyle={{
                          backgroundColor: "#fff",
                          border: "1px solid #e5e7eb",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="netWorth"
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={{ fill: "#2563eb" }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Analytics Section */}
              <AnalyticsSection entries={entries} accounts={accounts} />

              {/* Breakdown */}
              {currentEntry && (
                <div
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "0.5rem",
                    border: "1px solid #e5e7eb",
                    padding: "1.5rem",
                  }}
                >
                  <h2
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: "600",
                      marginBottom: "1rem",
                    }}
                  >
                    Account Breakdown
                  </h2>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: "1rem",
                    }}
                  >
                    <div>
                      <p style={{ fontSize: "0.875rem", color: "#888" }}>
                        Cash Accounts
                      </p>
                      <p
                        style={{
                          fontSize: "1.875rem",
                          fontWeight: "bold",
                          color: "#10b981",
                        }}
                      >
                        {formatCurrency(currentEntry.totalCash)}
                      </p>
                    </div>
                    <div>
                      <p style={{ fontSize: "0.875rem", color: "#888" }}>
                        Credit Cards
                      </p>
                      <p
                        style={{
                          fontSize: "1.875rem",
                          fontWeight: "bold",
                          color: "#ef4444",
                        }}
                      >
                        {formatCurrency(currentEntry.totalCredit)}
                      </p>
                    </div>
                    <div>
                      <p style={{ fontSize: "0.875rem", color: "#888" }}>
                        Investments
                      </p>
                      <p
                        style={{
                          fontSize: "1.875rem",
                          fontWeight: "bold",
                          color: "#2563eb",
                        }}
                      >
                        {formatCurrency(currentEntry.totalInvestments)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )
        )}

        {/* Logout */}
        <div style={{ marginTop: "2rem", textAlign: "center" }}>
          <button
            onClick={logout}
            style={{
              fontSize: "0.875rem",
              color: "#888",
              textDecoration: "underline",
              cursor: "pointer",
              background: "none",
              border: "none",
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpendDashboard;
